import React, { useState, useCallback, useEffect } from "react";
import { Card, CardContent } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Avatar, AvatarFallback } from "src/components/ui/Avatar";
import { TutorExtendedProfileData } from "src/interfaces/TutorProfile";
import AuthenticatedImage from "src/components/ui/AuthenticatedImage";
import { Camera, Star, Clock, DollarSign, Edit } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/Tooltip";

interface TutorProfileCardSectionProps {
  profileData: TutorExtendedProfileData;
  handleOpenPhotoUpload: (type: 'profile' | 'gallery') => void;
  onEditBio?: () => void;
  onViewProfile?: () => void;
}

const TutorProfileCardSection: React.FC<TutorProfileCardSectionProps> = ({
  profileData,
  handleOpenPhotoUpload,
  onEditBio,
  onViewProfile
}) => {
  // State for image loading to prevent flickering
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageLoaded(false);
    setImageError(true);
  }, []);

  // Get initials for fallback
  const getInitials = () => {
    const firstName = profileData.firstName || "";
    const lastName = profileData.lastName || "";
    if (!firstName && !lastName) return "T";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Reset image state when profile picture URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [profileData.profilePictureUrl]);

  // Check if we should show profile picture or fallback
  const hasValidProfilePicture = profileData.profilePictureUrl && profileData.profilePictureUrl.trim() !== '';
  const shouldShowImage = hasValidProfilePicture && !imageError;

  // Debug logging
  console.log('TutorProfileCardSection - profileData.profilePictureUrl:', profileData.profilePictureUrl);
  console.log('TutorProfileCardSection - hasValidProfilePicture:', hasValidProfilePicture);
  console.log('TutorProfileCardSection - shouldShowImage:', shouldShowImage);
  console.log('TutorProfileCardSection - imageError:', imageError);
  console.log('TutorProfileCardSection - imageLoaded:', imageLoaded);
  console.log('TutorProfileCardSection - initials:', getInitials());

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center">
          {/* Profile Photo */}
          <div className="relative mb-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div 
                    className="cursor-pointer hover:ring-2 hover:ring-rfpurple-300 rounded-full transition-all duration-200"
                    onClick={onViewProfile}
                  >
                    <Avatar className="h-24 w-24">
                      {shouldShowImage ? (
                        <>
                          {/* Show fallback while loading to prevent empty space */}
                          {!imageLoaded && (
                            <AvatarFallback className="text-lg absolute inset-0">
                              {getInitials()}
                            </AvatarFallback>
                          )}
                          <AuthenticatedImage
                            src={profileData.profilePictureUrl}
                            alt="Profile picture"
                            className={`w-full h-full object-cover rounded-full transition-opacity duration-200 ${
                              imageLoaded ? 'opacity-100' : 'opacity-0'
                            }`}
                            fallback={
                              <AvatarFallback className="text-lg">
                                {getInitials()}
                              </AvatarFallback>
                            }
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                          />
                        </>
                      ) : (
                        // Always show fallback when no profile picture URL or when image fails to load
                        <AvatarFallback className="text-lg">
                          {getInitials()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </div>
                </TooltipTrigger>
                <TooltipContent>View full profile</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              size="sm"
              className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
              onClick={() => handleOpenPhotoUpload('profile')}
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>

          {/* Name and Basic Info */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <h2 
                  className="text-xl font-semibold mb-1 cursor-pointer hover:text-rfpurple-600 transition-colors"
                  onClick={onViewProfile}
                >
                  {profileData.firstName} {profileData.lastName}
                </h2>
              </TooltipTrigger>
              <TooltipContent>View full profile</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {profileData.bio && (
            <div className="flex items-center justify-center mt-2">
              <p className="text-gray-700 text-center mr-2 mb-0">{profileData.bio}</p>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 ml-1 rounded-full hover:bg-gray-100"
                aria-label="Edit Bio"
                onClick={typeof onEditBio === 'function' ? onEditBio : undefined}
              >
                <Edit className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center justify-center space-x-4 mb-4 text-sm">
            {profileData.rating && (
              <div className="flex items-center space-x-1">
                <Star className="h-4 w-4 text-yellow-500 fill-current" />
                <span className="font-medium">{profileData.rating}</span>
              </div>
            )}
            
          </div>

          {/* Verification Status */}
          {profileData.verification_status && (
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-4 ${
              profileData.verification_status === 'verified' 
                ? 'bg-green-100 text-green-800' 
                : profileData.verification_status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {profileData.verification_status === 'verified' ? '✓ Verified' : 
               profileData.verification_status === 'pending' ? '⏳ Pending Verification' : 
               'Not Verified'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TutorProfileCardSection;